
import { useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Permission, Resource, PermissionType, Role } from "./types";
import { loadPermissions } from "./permissionService";
import { debugLogStorageContent } from "./permissionService";
import { 
  usePermissionState,
  usePermissionLoader,
  usePermissionUpdater,
  useDirtyCheck,
  usePermissionChecker
} from "./hooks";

// Re-export types from the types file to maintain the same public API
export * from "./types";
export { defaultPermissions, PERMISSIONS_STORAGE_KEY } from "./defaultPermissions";

export const useRolePermissions = () => {
  const { user } = useAuth();
  
  // Get state management hooks
  const {
    permissions,
    setPermissions,
    initialPermissions,
    setInitialPermissions,
    isLoading,
    setIsLoading,
    isDirty,
    setIsDirty,
    loadAttempts,
    setLoadAttempts,
    lastSaveTime,
    setLastSaveTime,
    initialized,
    didSaveRef,
    storageAvailable,
    initializationAttempts,
    databaseInitialized
  } = usePermissionState();
  
  // Debug log function
  const logPermissionsState = useCallback(() => {
    console.log("[useRolePermissions] Current permissions state:", {
      count: permissions?.length || 0,
      initialCount: initialPermissions?.length || 0,
      isLoading,
      isDirty,
      initialized: initialized.current,
      didSave: didSaveRef.current,
      loadAttempts,
      databaseInitialized: databaseInitialized.current,
      lastSaveTime
    });
    
    // Log storage content for comparison
    debugLogStorageContent();
  }, [permissions, initialPermissions, isLoading, isDirty, loadAttempts, lastSaveTime, initialized, didSaveRef, databaseInitialized]);

  // Load permissions from storage when the hook is first used
  usePermissionLoader({
    initialized,
    storageAvailable,
    initializationAttempts,
    databaseInitialized,
    setIsLoading,
    setLoadAttempts,
    setPermissions,
    setInitialPermissions,
    logPermissionsState
  });

  // Get permission update functions
  const { savePermissions, updatePermission } = usePermissionUpdater({
    user,
    permissions,
    setPermissions,
    setInitialPermissions,
    didSaveRef,
    setLastSaveTime,
    logPermissionsState
  });

  // Check if permissions have been modified
  useDirtyCheck({
    permissions,
    initialPermissions,
    isLoading,
    isDirty,
    setIsDirty,
    initialized,
    didSaveRef
  });

  // Get permission checking functions
  const { hasPermission } = usePermissionChecker({
    user,
    permissions,
    isLoading,
    initialized
  });

  return {
    permissions,
    isLoading,
    isDirty,
    updatePermission,
    savePermissions,
    hasPermission,
    initialized: initialized.current,
    loadAttempts,
    databaseInitialized: databaseInitialized.current,
    lastSaveTime
  };
};
